// src/hooks/useLMSAccess.js
// Central hook for LMS staff access control
// Manages staff sessions in localStorage separately from institution login
// Admin (institution account) always has full access — no lock ever

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';

// All LMS pages
export const LMS_PAGES = {
  dashboard:     { label:'Dashboard',     icon:'🏠', description:'Main overview and stats'          },
  students:      { label:'Students',      icon:'👩‍🎓', description:'Student management'                },
  classes:       { label:'Classes',       icon:'📚', description:'Class creation and management'     },
  timetable:     { label:'Timetable',     icon:'📅', description:'Weekly class schedule'             },
  teachers:      { label:'Teachers',      icon:'👨‍🏫', description:'Teacher profiles and subjects'     },
  assignments:   { label:'Assignments',   icon:'📋', description:'Create and manage assignments'     },
  attendance:    { label:'Attendance',    icon:'✅', description:'Mark and view attendance'           },
  courses:       { label:'Courses',       icon:'🎓', description:'Course content and materials'      },
  exams:         { label:'Exams',         icon:'📝', description:'Exam schedule and results'         },
  calendar:      { label:'Calendar',      icon:'🗓️', description:'Academic calendar and events'      },
  library:       { label:'Library',       icon:'📖', description:'Book catalog and borrowing'        },
  fees:          { label:'Fees',          icon:'💰', description:'Fee management and payments'       },
  feed:          { label:'Feed',          icon:'📢', description:'Announcements and posts'           },
  events:        { label:'Events',        icon:'🎉', description:'Institution events'                },
  notices:       { label:'Notices',       icon:'📌', description:'Official notices'                  },
  reports:       { label:'Reports',       icon:'📊', description:'Analytics and reports'             },
  notifications: { label:'Notifications', icon:'🔔', description:'Push notification management'      },
  settings:      { label:'Settings',      icon:'⚙️', description:'Institution settings and access'   },
};

// Default role templates
export const ROLE_TEMPLATES = {
  teacher: {
    name:'Teacher', emoji:'👨‍🏫', color:'#4F8EF7',
    pages:['dashboard','classes','timetable','teachers','assignments','attendance','courses','exams','calendar','feed','events','notices'],
  },
  librarian: {
    name:'Librarian', emoji:'📖', color:'#10B981',
    pages:['dashboard','library','calendar','notices'],
  },
  hod: {
    name:'Head of Department', emoji:'🎓', color:'#7C3AED',
    pages:['dashboard','students','classes','timetable','teachers','assignments','attendance','courses','exams','calendar','feed','events','notices','reports'],
  },
  accountant: {
    name:'Accountant', emoji:'💰', color:'#F59E0B',
    pages:['dashboard','fees','reports','notices'],
  },
  coordinator: {
    name:'Coordinator', emoji:'📋', color:'#EC4899',
    pages:['dashboard','students','classes','timetable','assignments','attendance','exams','calendar','feed','events','notices','reports'],
  },
};

const STORAGE_KEY = 'lms_staff_session';

// ── Context ───────────────────────────────────────────
const LMSAccessContext = createContext(null);

export function LMSAccessProvider({ children, institutionId, isAdmin }) {
  const [staffSession, setStaffSession]   = useState(null); // logged-in staff member
  const [loading,      setLoading]        = useState(true);

  // On mount — restore session from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // If institutionId is known, verify it matches
        // If institutionId is null (staff login page, no admin session), still restore
        if (!institutionId || parsed.institution_id === institutionId) {
          setStaffSession(parsed);
        } else {
          // Different institution — clear
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch(_) {}
    setLoading(false);
  }, [institutionId]);

  // Staff login via username + PIN
  const staffLogin = useCallback(async (username, pin) => {
    try {
      // Use institutionId from props, or fall back to stored value
      const instId = institutionId || localStorage.getItem('lms_institution_id');
      const { data, error } = await supabase.rpc('verify_staff_login', {
        p_institution_id: instId,
        p_username:       username.toLowerCase().trim(),
        p_pin:            pin,
      });
      if (error) throw error;
      if (!data.success) return { success:false, error: data.error };

      const session = {
        ...data,
        institution_id: institutionId,
        logged_in_at:   new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      setStaffSession(session);
      return { success:true };
    } catch(e) {
      return { success:false, error: e.message || 'Login failed' };
    }
  }, [institutionId]);

  // Staff logout
  const staffLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setStaffSession(null);
  }, []);

  // Check if current staff/admin can access a page
  const canAccess = useCallback((pageKey) => {
    if (isAdmin) return true;                       // Admin always has full access
    if (!staffSession) return false;                // Not logged in
    if (staffSession.is_admin) return true;         // Staff marked as admin
    const pages = staffSession.pages || {};
    // Dashboard always accessible if logged in
    if (pageKey === 'dashboard') return true;
    return pages[pageKey]?.can_access === true;
  }, [isAdmin, staffSession]);

  // Check if can edit (not view only)
  const canEdit = useCallback((pageKey) => {
    if (isAdmin) return true;
    if (!staffSession) return false;
    if (staffSession.is_admin) return true;
    if (pageKey === 'dashboard') return false; // dashboard is always view only for staff
    const page = staffSession.pages?.[pageKey];
    return page?.can_edit === true;
  }, [isAdmin, staffSession]);

  return (
    <LMSAccessContext.Provider value={{
      staffSession, loading,
      staffLogin, staffLogout,
      canAccess, canEdit,
      isAdmin,
    }}>
      {children}
    </LMSAccessContext.Provider>
  );
}

export const useLMSAccess = () => {
  const ctx = useContext(LMSAccessContext);
  // Return safe defaults if used outside provider (e.g. on staff login page)
  if (!ctx) return {
    staffSession: null, loading: false,
    staffLogin: async () => ({ success: false, error: 'No provider' }),
    staffLogout: () => {},
    canAccess: () => false,
    canEdit: () => false,
    isAdmin: false,
  };
  return ctx;
};